/**
 * The lower half of `Admin › Settings` — turns `5d` and `6b`.
 *
 * THERE IS NO `COMMISSIONER TOOLS` TAB ANY MORE. It held five cards: three that set what the
 * league runs on — league configuration, the Grid, ending the season — and two that report on it,
 * being what the committee has passed that the app has not applied yet, and what has been set by
 * hand against the rulebook. Splitting those from the deadlines and the draft-night clocks was a
 * distinction only the person who built it could hold. They are one act — deciding the shape of
 * the season — so they render as one page: deadlines and clocks, then the values, then the two
 * reports, then the once-a-year button.
 *
 * ONE PLACE LEAGUE CONFIGURATION LIVES, with every value tied to the rule that set it. A keeper
 * cap is not a preference; it is what a passed rule says. Editing it silently would let the app
 * disagree with its own rulebook, which is the thing the citation chips exist to prevent — so
 * each row names its rule, a proposal on the docket that would change the value says so inline in
 * orange, and an edit that contradicts an active rule is recorded as an override with a name on
 * it.
 *
 * THE SAME TABLE, TWO WAYS. `5d` is the table before a meeting: value, effect, governing rule.
 * `6b` is the table after one: this season, next season, the vote that set it, and its state. They
 * are the same object at two moments, so they are one control with a switch rather than two
 * screens — and the switch is worth having because the questions differ. Before the meeting you
 * ask "what is this and why"; afterwards you ask "what did we change and has it landed".
 *
 * `PASSED, NOT YET ACTIVE` IS THE LOUDEST CARD HERE, deliberately. A rule the league voted for
 * that the app does not implement is the failure nobody notices until a draft is underway, and
 * both halves look right on their own.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { LEAGUE_2026 } from '../domain/config.js';
import { GRID_ROUNDS, penaltyRound, maxKeeperYears } from '../domain/grid.js';
import { configFor, configRows, type SettingKey } from '../domain/effects.js';
import {
  DERIVED_STEPS, applyRollover, rolloverPlan, rolloverReview, type RolloverInput,
} from '../domain/rollover.js';
import {
  OverrideError, inForce, makeOverride, overrideNote, type Override,
} from '../domain/overrides.js';
import { faab, openingBalance } from '../domain/faab.js';
import { managerShort } from '../domain/identity.js';
import { Citations, Rule } from './RuleText.js';
import { RichView } from './Rich.js';
import { isRich, plainToRich } from '../domain/richText.js';
import { FARM_ROUNDS_TARGET, farmRoundsNow } from '../domain/overrides.js';
import { isRevoteWording } from '../domain/revote.js';
import { GMS } from '../store/session.js';
import { asFirestore, asMemoryStore } from '../store/provider.js';
import {
  atLeast, type DraftClocks, type DraftState, type FaabEntry, type LeagueSettings, type LeagueStore,
  type Role, type RuleProposal,
} from '../store/types.js';
import { ClockSettings } from './ClockSettings.js';
import { useLeagueRules } from './useLeagueRules.js';

const SEASON = 2026;

/** What each setting DOES, in the words a commissioner needs rather than the field name. */
const EFFECT: Partial<Record<SettingKey, string>> = {
  freeKeepers: 'slots before any tax · farm graduates count inside this',
  maxKeepers: 'tax included · a franchise tag consumes three of them',
  draftRounds: 'matches roster size',
  rosterSize: 'starters and bench, excluding IR',
  maxFarmPlayers: 'college players a GM may hold at once · graduation adds ten rounds to his '
    + 'keeper cost',
  farmPicksPerYear: 'a GM who wins one stops bidding · no limit lets him keep bidding until his '
    + 'slots fill',
  farmRounds: 'nomination rounds in the farm auction',
  irSlots: 'do not count against the roster',
  sleeperMinRound: 'the earliest round that can earn the sleeper reward',
};

const GOVERNS: Partial<Record<SettingKey, string[]>> = {
  freeKeepers: ['2020-III-1'],
  maxKeepers: ['2023-keeper-expansion-eight'],
  draftRounds: ['2017-IV-1'],
  rosterSize: ['2019-III-1'],
  maxFarmPlayers: ['2022-farm-auction-declarations'],
  farmPicksPerYear: ['2017-II-5'],
  irSlots: ['2022-ir-expansion'],
  sleeperMinRound: ['2017-II-2'],
};

export function CommissionerTools(
  { gm, role, store }: { gm: string; role: Role; store: LeagueStore },
) {
  const canEdit = atLeast(role, 'commissioner');
  /* One read of the docket and the log for the whole page; the deadlines card shares it. */
  const { proposals, overrides, passed } = useLeagueRules(store);

  /*
   * THE CLOCKS SAVE ON THEIR OWN. They live in the settings document with the deadlines, and used
   * to save with the deadlines' button — which was two cards apart from the fields it committed.
   * Rev B draws them as their own card, so they get their own foot: same document, written whole
   * with the deadlines as they stand, and a Save that sits under the fields it saves.
   */
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [clocks, setClocks] = useState<DraftClocks | undefined>(undefined);
  const [clocksSaved, setClocksSaved] = useState(false);
  useEffect(() => {
    let live = true;
    const load = () => store.settings(SEASON).then((x) => {
      if (live) { setSettings(x); setClocks(x?.clocks); }
    });
    void load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'settings') void load(); });
    return () => { live = false; off(); };
  }, [store]);
  const clocksDirty = JSON.stringify(clocks ?? null) !== JSON.stringify(settings?.clocks ?? null);
  async function saveClocks() {
    const next: LeagueSettings = {
      ...(settings ?? {
        season: SEASON, keeperDeadline: null, declarationsLocked: false, preferenceDeadline: null,
        proposalDeadline: null, committeeDate: null, draftDate: null,
      }),
      clocks,
      updatedAt: new Date().toISOString(),
      updatedBy: gm,
    };
    await store.saveSettings(next);
    setSettings(next);
    setClocksSaved(true);
    setTimeout(() => setClocksSaved(false), 2500);
  }

  /** A proposal still on the docket that WOULD change a value — the orange chip. */
  const pending = useMemo(() => {
    const m = new Map<SettingKey, { title: string; to: number }>();
    for (const p of proposals) {
      if (p.status !== 'docketed' || !p.effect) continue;
      for (const s of p.effect.sets ?? []) m.set(s.key, { title: p.title, to: s.to });
    }
    return m;
  }, [proposals]);

  const rows = useMemo(() => configRows(SEASON, passed), [passed]);
  const ruleFarmRounds = rows.find((r) => r.key === 'farmRounds')?.now ?? 2;
  const farmRounds = farmRoundsNow(overrides, ruleFarmRounds);

  async function setFarmRounds(n: number) {
    /* The rule's own value puts the hand-set one back rather than recording a row that says nothing. */
    if (n === ruleFarmRounds) {
      const held = inForce(overrides).find((o) => o.target === FARM_ROUNDS_TARGET);
      if (held) {
        await store.saveOverride({ ...held, revertedAt: new Date().toISOString(), revertedBy: gm.toUpperCase() });
      }
      return;
    }
    await store.saveOverride({
      id: `ovr-farmrounds-${new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 17)}`,
      season: SEASON,
      target: FARM_ROUNDS_TARGET,
      label: 'Farm rounds',
      ruleValue: String(ruleFarmRounds),
      setTo: String(n),
      reason: 'Set by hand on the configuration table.',
      by: gm.toUpperCase(),
      at: new Date().toISOString(),
      cites: ['2015-IV-2'],
    });
  }
  const live = useMemo(() => configFor(SEASON, passed), [passed]);

  return (
    <div className="app admin contd">
      {/*
        TWO CARDS, SIDE BY SIDE. The segmented "Values and rules | Seasons and votes" switch was
        cut in rev B: they are different questions, so they are different cards. Values is what the
        league runs on today and the one editor; Seasons is the same settings as a version history,
        with the vote that set each. Below 900px they stack.
      */}
      <div className="twoup">
        <section className={`card values${canEdit ? ' writable' : ''}`}>
          <div className="cardhead"><h3>League configuration — values and rules</h3></div>
          <ul className="valrows">
            {rows.map((r) => (
              <li key={r.key}>
                <span className="valname">{r.label}</span>
                <span className="valnum">
                  {r.key === 'farmRounds' && canEdit
                    ? <FarmRoundsField ruleValue={r.now} now={farmRounds} onSave={setFarmRounds} />
                    : <b>{r.key === 'farmRounds' ? farmRounds : r.now}</b>}
                </span>
                <span className="valeff">
                  <span>{EFFECT[r.key] ?? ''}</span>
                  {pending.has(r.key) && (
                    <span className="chip docket" title={pending.get(r.key)!.title}>
                      docket → {pending.get(r.key)!.to}
                    </span>
                  )}
                  {r.key === 'farmRounds' && farmRounds !== r.now && (
                    <span className="chip byhand" title="Set by hand, not by a vote">
                      set by hand · rules say {r.now}
                    </span>
                  )}
                </span>
                <span className="valrule">
                  {GOVERNS[r.key] ? <Citations ids={GOVERNS[r.key]!} /> : <span className="mut">—</span>}
                </span>
              </li>
            ))}
            {/*
              RULEBOOK CONSTANTS. Franchise tag and the trade windows are facts the rulebook fixes
              and no vote has ever moved through a setting; there is no row behind them a docket
              item could change. Shown as inert facts under their own rule so nobody looks for the
              editor, with § doors to where they come from.
            */}
            <li className="valband"><span className="peyebrow">Rulebook constants — no setting behind them</span></li>
            <li className="inert">
              <span className="valname">Franchise tag</span>
              <span className="valnum"><b>3 slots</b></span>
              <span className="valeff"><span>holds a player at his own round</span></span>
              <span className="valrule"><Citations ids={['2025-P05']} /></span>
            </li>
            <li className="inert">
              <span className="valname">Trade window</span>
              <span className="valnum"><b>24h / 48h</b></span>
              <span className="valeff"><span>in season / offseason</span></span>
              <span className="valrule"><Citations ids={['2015-II-3', '2017-II-3']} /></span>
            </li>
          </ul>
          <p className="cardfoot fact">
            Seven settings a vote can change; one editor — farm rounds, because the experiment
            lapses unless re-voted. The mono cells are inert.
          </p>
          <TaxSlots config={live} />
        </section>

        <section className="card seasons">
          <div className="cardhead">
            <h3>Seasons and votes</h3>
            <span className="mut">a season is a version, not an edit</span>
          </div>
          <table className="seastab">
            <thead>
              <tr>
                <th>Setting</th><th className="num">{SEASON}</th><th className="num">{SEASON + 1}</th><th>Set by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <th scope="row">{r.label}</th>
                  <td className="num">{r.now}</td>
                  <td className="num">
                    <b className={r.direction}>{r.next}</b>
                    {r.direction !== 'same' && (
                      <span className="dir" aria-hidden="true">{r.direction === 'up' ? '↑' : '↓'}</span>
                    )}
                  </td>
                  <td className="mut">
                    {r.setBy ?? 'unchanged'}
                    {r.docketRef && ` · ${r.docketRef}`}
                    {r.state === 'partly-built' && ` · ${r.built!.done} of ${r.built!.of} built`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="cardfoot fact">Earlier seasons keep the values they ran under.</p>
        </section>
      </div>

      <ClockSettings clocks={clocks} canEdit={canEdit} onChange={setClocks}
        foot={canEdit ? (
          <>
            <span className="mut">{clocksSaved ? 'Saved' : clocksDirty ? 'Unsaved changes' : 'Saved with the settings'}</span>
            <button type="button" className="btn primary" disabled={!clocksDirty} onClick={() => void saveClocks()}>
              Save clocks
            </button>
          </>
        ) : null} />

      <GridEditor canEdit={canEdit} gm={gm}
        overrides={overrides}
        onSave={(o) => void store.saveOverride(o)} />

      <OverrideLog overrides={overrides} canEdit={canEdit} gm={gm}
        onRevert={(o) => void store.saveOverride({
          ...o, revertedAt: new Date().toISOString(), revertedBy: gm.toUpperCase(),
        })} />

      <EndOfSeason gm={gm} canEdit={canEdit} proposals={proposals} store={store} />

    </div>
  );
}

/** A number and a Save, for the one value on this table a commissioner may set by hand. */
function FarmRoundsField(
  { ruleValue, now, onSave }: { ruleValue: number; now: number; onSave: (n: number) => void },
) {
  const [text, setText] = useState(String(now));
  useEffect(() => { setText(String(now)); }, [now]);
  const n = Number(text);
  const ok = Number.isInteger(n) && n >= 1 && n <= 4;
  return (
    <span className="farmroundsfield">
      <input type="number" min={1} max={4} inputMode="numeric" aria-label="Farm rounds"
        value={text} onChange={(e) => setText(e.target.value)} />
      <button type="button" className="btn small" disabled={!ok || n === now}
        onClick={() => onSave(n)}>
        Save
      </button>
      {!ok && <span className="mut">1 to 4</span>}
      {ok && n === now && now !== ruleValue && <span className="mut">by hand</span>}
    </span>
  );
}

/**
 * The luxury tax, a field per taxed slot.
 *
 * GENERATED FROM THE TWO ROWS ABOVE IT rather than typed, which is `5d`'s own argument: the taxed
 * slots are everything between Free keepers and Maximum, so raising the maximum should make
 * fields appear pre-filled from the step rather than requiring the control to be redrawn. One
 * string to parse was the old shape and it could not survive the committee moving the cap.
 */
function TaxSlots({ config }: { config: typeof LEAGUE_2026 }) {
  const step = 22;
  const slots = [];
  for (let n = config.freeKeepers + 1; n <= config.maxKeepers; n += 1) slots.push(n);

  return (
    <div className="taxslots">
      <div className="tshead">
        <b>Luxury tax</b>
        <span className="mut">
          {faab(config.luxuryTaxByKeeperCount[config.freeKeepers + 1] ?? step)} to{' '}
          {faab(config.luxuryTaxByKeeperCount[config.maxKeepers] ?? step * slots.length)}
          {' · '}{slots.length} taxed slot{slots.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="tsrow">
        {slots.map((n, i) => (
          <span className="tsslot" key={n}>
            <span className="mut">{n}{ord(n)}</span>
            <b>{config.luxuryTaxByKeeperCount[n] ?? (i + 1) * step}</b>
          </span>
        ))}
      </div>
      <p className="note">
        Slots {config.freeKeepers + 1}–{config.maxKeepers} come from Free keepers{' '}
        {config.freeKeepers} and Maximum {config.maxKeepers}. Raise the maximum and fields appear,
        pre-filled from the {faab(step)} step. <Rule id="2023-keeper-expansion-eight">§</Rule>
      </p>
    </div>
  );
}

/**
 * The Grid, editable, with the thing that makes editing it safe: a count of what it would break.
 *
 * A penalty round is not a number in a table — it is the price of every keeper in the league, and
 * moving one re-prices all of them. So the editor states how many players would change cost and
 * how many would fall out of eligibility BEFORE it saves, and keeps `Reset to the rulebook`
 * beside it so a wrong edit is one press from undone.
 */
/** One document the Grid editor will write: a new override, or a standing one put back. */
type GridWrite = { kind: 'override' | 'revert'; round: number; override: Override };

function GridEditor(
  { canEdit, gm, overrides, onSave }: {
    canEdit: boolean; gm: string; overrides: readonly Override[];
    onSave: (o: Override) => void;
  },
) {
  const rounds = Array.from({ length: GRID_ROUNDS }, (_, i) => i + 1);
  const [edited, setEdited] = useState<Record<number, number | null>>({});
  const [reason, setReason] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = Object.keys(edited).length > 0;

  /** What the log already says about this table, so an edited round shows it. */
  const live = useMemo(
    () => new Map(inForce(overrides).filter((o) => o.target.startsWith('grid:'))
      .map((o) => [Number(o.target.slice(5)), o])),
    [overrides],
  );

  /** The value a cell shows before any edit this sitting: the override in force, else the rule. */
  const baseOf = (r: number): number | null => {
    const held = live.get(r);
    return held ? (held.setTo === 'n/e' ? null : Number(held.setTo)) : penaltyRound(r);
  };

  /**
   * THE WRITES, COMPUTED BEFORE ANY OF THEM EXIST. One override per round changed, against the
   * rulebook's value for that round — not against the override already in force, because the log
   * records departures from the RULE, which is the thing a future reader is checking against.
   *
   * A ROUND PUT BACK TO THE RULE IS A REVERT, NOT AN OVERRIDE. Typing the rulebook's own value
   * over a hand-set one used to be dropped from this list — `makeOverride` refuses a row that
   * says nothing — which left the hand-set value in force and the commissioner staring at a
   * table that would not take his correction. The write for that case is the one Set by hand
   * makes: the standing override gets `revertedAt`, and the rule applies again.
   */
  const writes = useMemo(() => {
    const now = new Date().toISOString();
    const out: GridWrite[] = [];
    for (const [k, v] of Object.entries(edited)) {
      const r = Number(k);
      const was = penaltyRound(r);
      if (v === baseOf(r)) continue;
      const held = live.get(r);
      if (v === was && held) {
        out.push({ kind: 'revert', round: r, override: { ...held, revertedAt: now, revertedBy: gm.toUpperCase() } });
        continue;
      }
      try {
        out.push({
          kind: 'override',
          round: r,
          override: makeOverride({
            target: `grid:${r}`,
            label: `The Grid — round ${r}`,
            ruleValue: was === null ? 'n/e' : String(was),
            setTo: v === null ? 'n/e' : String(v),
            reason: reason.trim() || '(reason to follow)',
            cites: ['2019-II-1'],
          }, gm, SEASON, now, `grid-${r}-${now}`),
        });
      } catch {
        /* Back to the rule's own value with nothing in force: there is nothing to write. */
      }
    }
    return out.sort((a, b) => a.round - b.round);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseOf reads `live`, listed
  }, [edited, reason, gm, live]);

  /**
   * Save the edit AS OVERRIDES, one row per round changed. Not as a new Grid: the Grid is what
   * 2019-II-1 says it is, and a commissioner typing into it is departing from the rulebook. The
   * honest record is a row saying what the rule gave, what he set instead and why.
   */
  function commit() {
    try {
      setErr(null);
      if (!reason.trim()) throw new OverrideError('A reason is required.');
      for (const w of writes) {
        onSave(w.kind === 'override' ? { ...w.override, reason: reason.trim() } : w.override);
      }
      setEdited({});
      setReason('');
      setPreviewing(false);
    } catch (e) {
      setErr(e instanceof OverrideError ? e.message : 'That did not save.');
    }
  }

  const discard = () => { setEdited({}); setReason(''); setPreviewing(false); setErr(null); };

  const impact = useMemo(() => {
    let changed = 0;
    let ineligible = 0;
    for (const r of rounds) {
      const now = r in edited ? edited[r]! : baseOf(r);
      const was = baseOf(r);
      if (now !== was) changed += 1;
      if (was !== null && now === null) ineligible += 1;
    }
    return { changed, ineligible };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseOf reads `live`, listed
  }, [edited, rounds, live]);

  return (
    <section className={`card gridcard${dirty ? ' editing' : ''}`}>
      <div className="cardhead">
        <h3>The Grid — {dirty ? 'editing' : 'keeper price map'}</h3>
        {canEdit && !dirty && <span className="pill amber mono">commissioner-writable</span>}
        {canEdit && (
          <button type="button" className={`linkbtn${dirty ? ' danger' : ''}`} disabled={!dirty}
            onClick={discard}>
            Reset to the rulebook
          </button>
        )}
      </div>

      <div className="gridbody">
        <div className="gridmap">
          {rounds.map((r) => {
            const held = live.get(r);
            const v = r in edited ? edited[r]! : baseOf(r);
            const off = v !== penaltyRound(r);
            return (
              <label className={`gmcell${v === null ? ' ne' : ''}${off ? ' off' : ''}`} key={r}
                title={held ? overrideNote(held) : off ? `rules say ${penaltyRound(r) ?? 'n/e'}` : undefined}>
                <span className="rd">Rd {r}</span>
                <input value={v === null ? 'n/e' : String(v)} disabled={!canEdit}
                  aria-label={`Penalty round for round ${r}`}
                  onChange={(e) => {
                    const t = e.target.value.trim().toLowerCase();
                    const n = t === 'n/e' || t === '' ? null : Number(t);
                    setEdited((prev) => ({ ...prev, [r]: Number.isNaN(n) ? null : n }));
                  }} />
              </label>
            );
          })}
        </div>
        <div className="gridnotes">
          <div className="gridnote">
            <span className="peyebrow">The floor</span>
            <p className="fact">
              Round 16 and later all forfeit a 12th — what caps a keeper at four years. <Rule id="2019-II-1" />
            </p>
          </div>
          <div className="gridnote">
            <span className="peyebrow">Max keeper length · derived</span>
            <b className="derived">{Math.max(...rounds.map((r) => maxKeeperYears(r)))} years</b>
            <p className="fact">walked from the map above — never entered</p>
          </div>
        </div>
      </div>

      {!dirty && (
        <p className="cardfoot fact">
          19 columns per 2019-II-1: rounds 1–2 n/e (no earlier pick to forfeit); rounds 17–19
          exist because a UDFA prices at round 17 and a farm graduate at NFL round + 10. An
          override-held cell is amber; its note is on hover. Editing any cell opens the
          preview-then-commit flow.
        </p>
      )}

      {dirty && !previewing && (
        <div className="gridflow">
          <p className="impact">
            Editing a penalty re-prices every eligible keeper in the league.{' '}
            <b>
              {impact.changed} round{impact.changed === 1 ? '' : 's'} change
              {impact.ineligible > 0 && `, ${impact.ineligible} become${impact.ineligible === 1 ? 's' : ''} ineligible`}.
            </b>{' '}
            Nothing is saved until you press preview.
          </p>
          <label className="whyfield">
            <span className="peyebrow">Why · required</span>
            <input value={reason} placeholder="e.g. 2027 rule vote, item 3 — escalation softened"
              aria-label="Why this override" onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="gridacts">
            <button type="button" className="btn" onClick={discard}>Discard</button>
            <button type="button" className="btn primary" disabled={!reason.trim() || writes.length === 0}
              onClick={() => setPreviewing(true)}>
              Preview the re-pricing
            </button>
          </div>
        </div>
      )}

      {dirty && previewing && (
        <div className="gridflow">
          {/* Shaped like End of season's writes list: one line per document, kind-tagged. */}
          <span className="peyebrow">{writes.length} row{writes.length === 1 ? '' : 's'} will be written</span>
          <ul className="writes">
            {writes.map((w) => (
              <li key={w.override.id}>
                <span className="wkind">{w.kind}</span>
                {w.kind === 'override'
                  ? <>{w.override.label}: {w.override.ruleValue} → <b>{w.override.setTo}</b> · {reason.trim()}</>
                  : <>{w.override.label}: {w.override.setTo} → <b>{w.override.ruleValue}</b> · back to the rule</>}
              </li>
            ))}
          </ul>
          <p className="fact">Every keeper priced off a changed round re-prices the moment this saves. Each row carries your name and can be reverted from Set by hand.</p>
          {err && <p className="estaxbad">{err}</p>}
          <div className="gridacts">
            <button type="button" className="btn" onClick={() => setPreviewing(false)}>Back</button>
            <button type="button" className="btn primary" onClick={commit}>Save as overrides</button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Ending the season — a review, not a button.
 *
 * Six things happen at once and the hard-to-undo ones are the ones nobody notices went wrong
 * until August. So everything it will do is listed with the counts it found, the problems it hit
 * are named before rather than discovered after, and it takes a typed confirmation.
 *
 * The second-commissioner requirement is drawn and is explicitly the DESIGNER'S PROPOSAL rather
 * than a rule — the drawing says so itself. It is stated on screen as a proposal so nobody quotes
 * it back as though the rulebook required it.
 */
function EndOfSeason(
  { gm, canEdit, proposals, store }: {
    gm: string; canEdit: boolean;
    /** The raw docket, not the digested `PassedRule` list — the re-vote needs the wording. */
    proposals: readonly RuleProposal[]; store: LeagueStore;
  },
) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [ledger, setLedger] = useState<FaabEntry[] | null>(null);
  const [nextDraft, setNextDraft] = useState<DraftState | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  /*
   * READ ON OPEN, not on mount. This card sits at the bottom of a settings page nobody opens to
   * end a season, and pulling the whole FAAB ledger every time somebody checks the keeper cap is
   * work for nothing.
   */
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    void Promise.all([store.faab(SEASON), store.draftState(SEASON + 1)])
      .then(([f, d]) => { if (live) { setLedger(f); setNextDraft(d); } });
    return () => { live = false; };
  }, [open, store]);

  /** Unspent per GM, from the ledger rather than from a stored balance. */
  const unspent = useMemo(() => {
    if (!ledger) return null;
    const out: Record<string, number> = Object.fromEntries(GMS.map((g) => [g, openingBalance(g)]));
    for (const e of ledger) {
      const g = e.gm.toUpperCase();
      out[g] = (out[g] ?? openingBalance(g)) + e.amount;
    }
    return out;
  }, [ledger]);

  /*
   * The finish comes from NEXT season's draft state, which is where the lottery keeps it — it is
   * the input to next year's order, not a property of the season being closed. Absent until a
   * commissioner has entered it, and the review says so rather than assuming an order.
   */
  const finishOrder = useMemo(
    () => (nextDraft?.finishOrder?.length ? nextDraft.finishOrder : []),
    [nextDraft],
  );

  const provisional = useMemo(
    () => proposals.filter((p) => p.status === 'provisional'),
    [proposals],
  );

  /*
   * NULL, NOT ZERO, for everything the rollover still has not counted.
   *
   * This dialog's entire value is that its figures are checkable — "38 players eligible · 4 aged
   * out" is a claim a commissioner can disagree with before it is written, which is why the
   * drawing puts the counts on it. Standing plausible numbers in for the ones nothing computes
   * would be the exact failure the rest of this app is built to avoid.
   *
   * FAAB and the provisional rules are now real, read from the ledger and the docket. The roster
   * archive is still not, so those lines still say so.
   */
  const review = useMemo(() => rolloverReview({
    season: SEASON,
    finishOrder,
    rosterCount: null,
    teams: LEAGUE_2026.teams,
    eligible: null,
    tradedPicks: null,
    swapRights: null,
    draftRounds: LEAGUE_2026.draftRounds,
    provisional: provisional.map((p) => ({ id: p.id, title: p.title })),
    unspent,
    faabBase: LEAGUE_2026.faabBase,
    faabRolloverRate: LEAGUE_2026.faabRolloverRate,
    /*
     * Derived, not invented — only what this screen can actually see from here. A missing finish
     * is the one that matters: next year's lottery has nothing to weight without it.
     */
    problems: finishOrder.length === LEAGUE_2026.teams ? [] : [
      `The ${SEASON} finish is not recorded, so ${SEASON + 1} starts with no lottery weighting.`,
    ],
  } satisfies RolloverInput), [finishOrder, provisional, unspent]);

  /** The exact rows, computed before any of them exist. */
  const plan = useMemo(() => (unspent === null ? [] : rolloverPlan({
    season: SEASON,
    unspent,
    faabBase: LEAGUE_2026.faabBase,
    faabRolloverRate: LEAGUE_2026.faabRolloverRate,
    provisional: provisional.map((p) => ({
      id: p.id, title: p.title, body: p.body, amends: p.amends, gm: p.gm,
    })),
    finishOrder,
    by: gm.toUpperCase(),
    now: new Date().toISOString(),
  })), [unspent, provisional, finishOrder, gm]);

  const ready = unspent !== null && typed.trim() === review.confirmWord && !busy;

  /**
   * Back the league up, then write.
   *
   * THE BACKUP IS NOT OPTIONAL AND NOT A CHECKBOX. There is no undo for this — no journal, no
   * dated snapshot — so the file the browser downloads a second before the first write is the
   * only thing that puts the league back. If it cannot be produced, nothing is written.
   */
  async function run() {
    setBusy(true);
    setFailed(null);
    try {
      backupFirst(store, SEASON);
      await applyRollover(plan, {
        saveFaab: (e) => store.saveFaab(e),
        saveProposal: (p) => store.saveProposal(p),
        saveSettings: (st) => store.saveSettings(st),
        saveDraftState: (d) => store.saveDraftState(d),
      });
      setDone(`${SEASON} is closed. ${plan.length} row${plan.length === 1 ? '' : 's'} written, `
        + `and ${SEASON + 1} is standing.`);
      setOpen(false);
      setTyped('');
    } catch (e) {
      /*
       * NAMES HOW FAR IT GOT. A rollover that stopped halfway is recoverable by looking at what
       * exists; one that reports "failed" and nothing else leaves a commissioner guessing whether
       * to run it again and double every FAAB allotment.
       */
      setFailed(e instanceof Error
        ? `${e.message} — some rows may already be written. Check the ${SEASON + 1} ledger before `
          + 'running this again, or restore the backup that just downloaded.'
        : 'The rollover did not finish.');
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit) return null;

  return (
    <section className={`card endseason${open ? ' open' : ''}`}>
      <div className="cardhead">
        <h3>{open ? `End the ${SEASON} season` : 'End of season'}</h3>
        <span className="peyebrow">once per year</span>
        {!open && (
          <button type="button" className="btn" onClick={() => { setOpen(true); setDone(null); }}>
            Review and end the {SEASON} season
          </button>
        )}
      </div>
      {!open && (
        <p className="cardfoot fact">
          Writes history and stands next season up. Review shows every row before anything is true.
          {done && <b className="good"> {done}</b>}
        </p>
      )}

      {open && (
        <div className="endreview">
          {/* THE STEPS, in run order, each with what it will find and whether it is derived. */}
          <div className="endblock">
            <span className="peyebrow">The steps</span>
            <ul className="endsteps">
              {review.steps.map((s) => (
                <li key={s.id}>
                  <b>{s.label}</b>
                  <span className="mut">{s.detail}</span>
                  {DERIVED_STEPS[s.id] && <span className="peyebrow derivedtag">derived</span>}
                  {s.cites && <Citations ids={s.cites} />}
                </li>
              ))}
            </ul>
          </div>

          {review.problems.length > 0 && (
            <div className="endproblems">
              <span className="peyebrow warn">
                {review.problems.length} thing{review.problems.length === 1 ? '' : 's'} need you first
              </span>
              {review.problems.map((p) => <p key={p} className="fact">{p}</p>)}
              <p className="fact warnline">
                Ending the season now writes {review.problems.length === 1 ? 'it' : 'them'} to
                history as {review.problems.length === 1 ? 'it stands' : 'they stand'}.
              </p>
            </div>
          )}

          {/*
            THE ROWS THEMSELVES, counted by the plan that will run — never hand-maintained. The
            steps above say what will happen; this says what will exist afterwards, one line per
            document, which is the only version a commissioner can disagree with before it is true.
          */}
          <div className="endblock">
            <span className="peyebrow">
              {unspent === null ? 'reading the ledger…' : `${plan.length} row${plan.length === 1 ? '' : 's'} will be written`}
            </span>
            <ul className="writes">
              {plan.map((w) => (
                <li key={`${w.kind}-${w.says}`}>
                  <span className="wkind">{w.kind === 'draftState' ? 'board' : w.kind}</span>
                  {w.says}
                </li>
              ))}
            </ul>
            <p className="fact">
              Counts marked <b>not counted yet</b> come from the roster archive, which this review
              does not read. It reports what it can compute and says so where it cannot.
            </p>
          </div>

          <div className="endfoot">
            <label className="endconfirm">
              <span className="fact">
                This is the version a commissioner can disagree with before it is true. Type{' '}
                <code>{review.confirmWord}</code> to confirm; a backup downloads first, and restoring
                it from Saved data is the only undo.
              </span>
              <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={review.confirmWord} />
            </label>
            {failed && <p className="estaxbad">{failed}</p>}
            <div className="gridacts">
              <button type="button" className="btn" onClick={() => { setOpen(false); setTyped(''); }}>Cancel</button>
              <button type="button" className="btn danger" disabled={!ready} aria-disabled={!ready}
                onClick={() => void run()}>
                {busy ? 'Writing…' : `End the ${SEASON} season`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Download the whole league as a file before anything irreversible happens.
 *
 * Deliberately the SAME shape Saved data writes and reads, so the undo path is one somebody has
 * already used rather than a special format that only this button produces. Throws if the store
 * cannot produce a snapshot, which stops the rollover — a rollover without a backup is the thing
 * this is here to prevent.
 */
function backupFirst(store: LeagueStore, season: number): void {
  const mem = asMemoryStore(store);
  const fs = asFirestore(store);
  const snap = mem ? mem.snapshot() : fs?.snapshot();
  if (!snap) throw new Error('No backup could be taken, so nothing was written');
  const blob = new Blob([JSON.stringify(snap, null, 1)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ffl-before-ending-${season}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/*
 * An override's two values are strings, and one side of a pair is often the other kind: the
 * rulebook's text is plain where the commissioner's replacement is markup. Both go through the
 * reader, so `plainToRich` is what stops the plain one arriving as a single unbroken paragraph
 * beside a formatted one.
 */
const asRich = (v: string) => (isRich(v) ? v : plainToRich(v));

const ord = (n: number) => (n % 10 === 1 && n !== 11 ? 'st'
  : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th');


/**
 * Every value somebody set by hand, and why.
 *
 * The half of an override that matters. The row it touches says it was moved; this says who moved
 * it, away from what, and for what reason — and it keeps a reverted one rather than deleting it,
 * because "this was overridden for two months in 2026 and put back" is exactly the kind of thing
 * a keeper-cost argument turns on.
 */
function OverrideLog(
  { overrides, canEdit, gm, onRevert }: {
    overrides: readonly Override[]; canEdit: boolean; gm: string;
    onRevert: (o: Override) => void;
  },
) {
  void gm;
  /*
   * WHICH ROW HAS ITS WORDING OPEN. One at a time — see the note on the cell below for why a
   * wording cannot simply live in the column.
   */
  const [wording, setWording] = useState<string | null>(null);
  /*
   * VALUES ONLY. A wording tidied for a re-vote (2019-I-2 §2) is stored as an override so the
   * edit is visible where the re-vote is — the Proposals page — and that is the only place it
   * belongs. Tim, 4 September, seeing two of them here a week after the room adopted both rules:
   * "there's no need to retain that information in the admin screens. that's a 100% rules
   * proposal function." So they are not this table's rows, in force or otherwise.
   */
  const values = overrides.filter((o) => !isRevoteWording(o));
  const live = new Set(inForce(values).map((o) => o.id));
  if (values.length === 0) return null;

  return (
    <section className="card byhand">
      <div className="cardhead">
        <h3>Set by hand</h3>
        <span className="pill grey mono">{live.size} in force · {values.length} recorded</span>
        <span className="mut">Putting a value back keeps the row.</span>
      </div>
      <table className="cfgtable byhandtab">
        <thead>
          <tr>
            <th>Setting</th><th className="num">Rules</th><th className="num">Set to</th>
            <th>Why</th><th>By</th><th />
          </tr>
        </thead>
        <tbody>
          {values.map((o) => (
            <Fragment key={o.id}>
            <tr className={live.has(o.id) ? '' : 'dim'}>
              <th scope="row">
                {o.label}
                {o.cites && <Citations ids={o.cites} />}
              </th>
              {/*
                A VALUE IS USUALLY A NUMBER AND SOMETIMES IT IS A RULE. A wording never renders in
                its cell — two thousand characters of markup in a `num` cell showed its own tags,
                crushed the columns and made the row taller than the screen. The cell says there IS
                one and opens it full width beneath, where prose has room and the table stays a table.
              */}
              {isRich(o.ruleValue) || isRich(o.setTo) ? (
                <>
                  <td className="num mut">—</td>
                  <td className="num">
                    <button type="button" className="linkbtn tiny"
                      aria-expanded={wording === o.id}
                      onClick={() => setWording(wording === o.id ? null : o.id)}>
                      {wording === o.id ? 'hide wording' : 'wording ▾'}
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="num mut">{o.ruleValue}</td>
                  <td className="num"><b>{o.setTo}</b></td>
                </>
              )}
              <td className="mut eff">{o.reason}</td>
              <td className="mut">
                {managerShort(o.by)} · {new Date(o.at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}
              </td>
              <td className="num">
                {o.revertedAt
                  ? <span className="mut">put back</span>
                  : canEdit && (
                    <button type="button" className="linkbtn" onClick={() => onRevert(o)}>Revert</button>
                  )}
              </td>
            </tr>
            {wording === o.id && (
              <tr className="ovwording">
                <td colSpan={6}>
                  <div className="ovwordinggrid">
                    <section>
                      <h4>What the rules say</h4>
                      <RichView html={asRich(o.ruleValue)} className="mut" />
                    </section>
                    <section>
                      <h4>What was set</h4>
                      <RichView html={asRich(o.setTo)} />
                    </section>
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
          ))}
        </tbody>
      </table>
      <p className="cardfoot fact">
        A wording never renders in its cell — it opens full width beneath the row, where prose has
        room and the table stays a table. Dim rows are no longer in force.
      </p>
    </section>
  );
}
